import { api } from '@/api/client';
import type { ApiResponse } from '@/types/auth';

export type AutomationEntityType =
  | 'portfolio'
  | 'campaign'
  | 'placement'
  | 'ad_group'
  | 'target'
  | 'advertised_product'
  | 'search_term'
  | 'st_harvest'
  | 'term_list';

export type AutomationRule = {
  id: string;
  profileId: string;
  name: string;
  entityType: AutomationEntityType;
  status: 'active' | 'paused';
  effectiveStatus?: 'active' | 'paused' | 'expired';
  adTypes: string[];
  executionMode: 'automatic' | 'manual';
  lookbackDays: number;
  excludeDays: number;
  conditionGroups: Array<Record<string, unknown>>;
  timezone: string;
  frequency: string;
  executionHour: number | null;
  executionHours?: number[] | null;
  weeklyDays: number[] | null;
  startDate: string | null;
  endDate: string | null;
  entityScope: string;
  placementKeys: string[] | null;
  ruleOptions?: Record<string, unknown> | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  conditionsSummary?: string;
  summary?: Record<string, unknown>;
  entities?: Array<{
    id: string;
    entityRef: string;
    placementKey: string | null;
    adProduct: string;
  }>;
};

export type AutomationEntityOption = {
  entityRef: string;
  campaignId: string;
  name: string | null;
  adProduct: string;
  state?: string | null;
  parentLabel?: string | null;
};

export type AutomationTermList = {
  id: string;
  profileId: string;
  name: string;
  kind: 'blacklist' | 'whitelist';
  match: 'exact' | 'phrase_contains';
  entityScope: string;
  autoApply: boolean;
  scheduleHour: number | null;
  timezone: string;
  status: string;
  ruleId: string | null;
  entries: string[];
  entities: Array<{ entityRef: string; adProduct: string }>;
  createdAt: string | null;
  updatedAt: string | null;
};

export type AutomationPending = {
  id: string;
  ruleId: string;
  ruleName: string | null;
  entityType: string;
  entityNoun?: string;
  entityKey: string;
  entityName: string | null;
  scheduledTime: string | null;
  plannedAction: Record<string, unknown> | null;
  beforeValue?: unknown;
  afterValue?: unknown;
  status: string;
  createdAt: string | null;
};

export type AutomationExecutionEvaluationLeaf = {
  metric?: string | null;
  operator?: string | null;
  valueType?: string | null;
  value?: unknown;
  valueTo?: unknown;
  actual?: unknown;
  actualPrevious?: unknown;
  compared?: unknown;
  passed?: boolean;
};

export type AutomationExecutionEvaluationGroup = {
  index?: number;
  kind?: string;
  passed?: boolean;
  summary?: string | null;
  leaves?: AutomationExecutionEvaluationLeaf[];
};

export type AutomationExecutionEvaluation = {
  matched?: boolean;
  matchedGroupIndex?: number | null;
  summary?: string | null;
  groups?: AutomationExecutionEvaluationGroup[];
  /** Placement perf keys (`tos_sales`, `ros_spend`, …) from the rule lookback. */
  placementMetrics?: Record<string, unknown> | null;
  /** Previous lookback window, only when the rule evaluated a relative condition. */
  placementMetricsPrevious?: Record<string, unknown> | null;
};

export type AutomationExecution = {
  id: string;
  ruleId: string;
  ruleName: string | null;
  entityType: string;
  entityNoun?: string;
  entityKey: string;
  entityName: string | null;
  actionType: string;
  beforeValue: unknown;
  afterValue: unknown;
  executionTime: string | null;
  /** Shared slot for all entities touched in the same rule run. */
  scheduledSlot?: string | null;
  status: string;
  errorMessage: string | null;
  dryRun: boolean;
  evaluation?: AutomationExecutionEvaluation | null;
  evaluationSummary?: string | null;
  revertStatus?: string | null;
  revertAt?: string | null;
  revertedAt?: string | null;
  revertError?: string | null;
};

export type AutomationExecutionOutcome = 'applied' | 'unmatched';

export type AutomationPendingRun = {
  ruleId: string;
  ruleName: string | null;
  scheduledSlot: string | null;
  entityType: string;
  entityNoun?: string;
  total: number;
  previewNames: string[];
  status: string;
  actionType: string;
  beforeValue?: unknown;
  afterValue?: unknown;
};

export const AUTOMATION_PENDING_PAGE_SIZE = 2000;

export type AutomationExecutionRun = {
  ruleId: string;
  ruleName: string | null;
  scheduledSlot: string | null;
  executionTime: string | null;
  entityType: string;
  entityNoun?: string;
  dryRun: boolean;
  total: number;
  statusCounts: Record<string, number>;
  previewNames: string[];
  actionType?: string | null;
};

export const automationApi = {
  listRules: (profileId: string, params?: Record<string, string | number | undefined>) =>
    api
      .get<ApiResponse<{ items: AutomationRule[]; total: number }>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/rules`,
        { params }
      )
      .then((r) => r.data),

  getRule: (profileId: string, ruleId: string) =>
    api
      .get<ApiResponse<AutomationRule>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/rules/${encodeURIComponent(ruleId)}`
      )
      .then((r) => r.data),

  createRule: (profileId: string, body: Record<string, unknown>) =>
    api
      .post<ApiResponse<AutomationRule>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/rules`,
        body
      )
      .then((r) => r.data),

  updateRule: (profileId: string, ruleId: string, body: Record<string, unknown>) =>
    api
      .put<ApiResponse<AutomationRule>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/rules/${encodeURIComponent(ruleId)}`,
        body
      )
      .then((r) => r.data),

  deleteRule: (profileId: string, ruleId: string) =>
    api
      .delete<ApiResponse<unknown>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/rules/${encodeURIComponent(ruleId)}`
      )
      .then((r) => r.data),

  duplicateRule: (profileId: string, ruleId: string) =>
    api
      .post<ApiResponse<AutomationRule>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/rules/${encodeURIComponent(ruleId)}/duplicate`
      )
      .then((r) => r.data),

  enableRule: (profileId: string, ruleId: string) =>
    api
      .post<ApiResponse<AutomationRule>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/rules/${encodeURIComponent(ruleId)}/enable`
      )
      .then((r) => r.data),

  disableRule: (profileId: string, ruleId: string) =>
    api
      .post<ApiResponse<AutomationRule>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/rules/${encodeURIComponent(ruleId)}/disable`
      )
      .then((r) => r.data),

  executeRule: (profileId: string, ruleId: string, body?: { dryRun?: boolean }) =>
    api
      .post<ApiResponse<unknown>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/rules/${encodeURIComponent(ruleId)}/execute`,
        body ?? {}
      )
      .then((r) => r.data),

  listPending: (profileId: string, params?: Record<string, string | number | undefined>) =>
    api
      .get<ApiResponse<{ items: AutomationPending[]; total: number }>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/pending`,
        { params }
      )
      .then((r) => r.data),

  listPendingRuns: (profileId: string, params?: Record<string, string | number | undefined>) =>
    api
      .get<ApiResponse<{ items: AutomationPendingRun[]; total: number }>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/pending-runs`,
        { params }
      )
      .then((r) => r.data),

  confirmPending: (profileId: string, pendingId: string) =>
    api
      .post<ApiResponse<unknown>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/pending/${encodeURIComponent(pendingId)}/confirm`
      )
      .then((r) => r.data),

  confirmPendingBatch: (profileId: string, ids: string[]) =>
    api
      .post<ApiResponse<{ results: unknown[] }>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/pending/confirm-batch`,
        { ids }
      )
      .then((r) => r.data),

  cancelPending: (profileId: string, pendingId: string) =>
    api
      .post<ApiResponse<unknown>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/pending/${encodeURIComponent(pendingId)}/cancel`
      )
      .then((r) => r.data),

  listExecutions: (
    profileId: string,
    params?: Record<string, string | number | undefined>
  ) =>
    api
      .get<ApiResponse<{ items: AutomationExecution[]; total: number }>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/executions`,
        { params }
      )
      .then((r) => r.data),

  listExecutionRuns: (
    profileId: string,
    params?: Record<string, string | number | undefined>
  ) =>
    api
      .get<ApiResponse<{ items: AutomationExecutionRun[]; total: number }>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/execution-runs`,
        { params }
      )
      .then((r) => r.data),

  listMetrics: (profileId: string, entityType: string) =>
    api
      .get<ApiResponse<Array<Record<string, unknown>>>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/metrics`,
        { params: { entity_type: entityType } }
      )
      .then((r) => r.data),

  listActions: (profileId: string, entityType: string) =>
    api
      .get<ApiResponse<Array<{ type: string; label: string; enabled: boolean; revertible?: boolean }>>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/actions`,
        { params: { entity_type: entityType } }
      )
      .then((r) => r.data),

  listEntities: (profileId: string, params?: Record<string, string | undefined>) =>
    api
      .get<
        ApiResponse<{
          items: Array<{
            entityRef?: string;
            campaignId: string;
            name: string | null;
            adProduct: string;
            state: string | null;
            parentLabel?: string | null;
            portfolioId?: string | null;
          }>;
          placementKeys: string[];
        }>
      >(`/api/v1/profiles/${encodeURIComponent(profileId)}/automation/entities`, { params })
      .then((r) => r.data),

  listTermLists: (profileId: string) =>
    api
      .get<ApiResponse<{ items: AutomationTermList[]; total: number }>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/term-lists`
      )
      .then((r) => r.data),

  createTermList: (profileId: string, body: Record<string, unknown>) =>
    api
      .post<ApiResponse<AutomationTermList>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/term-lists`,
        body
      )
      .then((r) => r.data),

  updateTermList: (profileId: string, listId: string, body: Record<string, unknown>) =>
    api
      .put<ApiResponse<AutomationTermList>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/term-lists/${encodeURIComponent(listId)}`,
        body
      )
      .then((r) => r.data),

  deleteTermList: (profileId: string, listId: string) =>
    api
      .delete<ApiResponse<unknown>>(
        `/api/v1/profiles/${encodeURIComponent(profileId)}/automation/term-lists/${encodeURIComponent(listId)}`
      )
      .then((r) => r.data),
};
